/**
 * HowLembraWorksMobile — MS368 mobile-native rewrite of How-it-works.
 *
 * Lucky's call after MS357-366 thrashed on responsive-share fixes:
 * scrap the shared chrome and build a phone-frame mockup specifically
 * for mobile. Each phase shows what the buyer would LITERALLY see on
 * their phone if they used Lembra — a chat screen, a card preview, a
 * confirm CTA, an iOS push notification stack. No desktop layouts
 * shrunk to phone width; everything here is drawn at mobile scale
 * from the start.
 *
 * Visual ingredients reused from real product surfaces:
 *   - CelebrationGlyphMorph (MS284) for the AI preloader cycle —
 *     same 5-frame glyph the live chat shows when the assistant is
 *     thinking
 *   - LEMBRA_VERB_VOCAB.conciergeRecommend — same 5 verbs paired
 *     1:1 with the glyph frames
 *   - Real vendor photos from /public/vendor-photos/ — same files
 *     the live catalogue serves
 *   - Coral / navy / butter / cream palette — Lembra brand standard
 *
 * Auto-cycles every 4s; tap the pill to advance manually. Honors
 * prefers-reduced-motion (no auto-cycle, only manual tap).
 *
 * Parent (HowLembraWorks.tsx) switches on useIsMobile and renders
 * this instead of the desktop scroll-pin version. Desktop is
 * completely untouched.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Wifi,
  BatteryFull,
  Signal,
  Send,
  Check,
  Sparkles,
  ArrowUp,
} from "lucide-react";
import { CelebrationGlyphMorph } from "@/components/lembra/CelebrationGlyph";
import { LEMBRA_VERB_VOCAB } from "@/components/lembra/ThinkingStrip";
import { useReducedMotion } from "@/hooks/use-reduced-motion";

const displayFamily = "Newsreader, Georgia, serif";
const monoFamily =
  "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
const sansFamily = "DM Sans, sans-serif";

type PhaseId = "brief" | "curate" | "approve" | "deliver";

interface Phase {
  id: PhaseId;
  label: string;
  title: string;
}

const PHASES: Phase[] = [
  { id: "brief", label: "Brief", title: "Brief the concierge" },
  { id: "curate", label: "Curate", title: "We surface a handful of options" },
  { id: "approve", label: "Approve", title: "Pick the one that feels right" },
  { id: "deliver", label: "Deliver", title: "It lands on their desk" },
];

const PHASE_INTERVAL_MS = 4200;

// ─── PHONE FRAME ────────────────────────────────────────────────
// Stylised iPhone chrome. Navy outer ring matches Lembra's brand
// nav stamp; a thin dark inner border reads as bezel. Single notch
// pill at the top. Inside is a fixed-aspect content area where each
// screen renders.

function PhoneFrame({
  children,
  variant = "dark",
}: {
  children: React.ReactNode;
  /** MS370: status-bar text colour. "dark" = navy (chat screens with
   *  cream background); "light" = white (Deliver lock-screen with
   *  photo wallpaper). Defaults to dark — only Deliver passes light. */
  variant?: "dark" | "light";
}) {
  return (
    <div
      className="relative mx-auto"
      style={{
        // Width chosen so the frame fits on a 360px viewport with
        // 24px page padding on each side (312px usable) and still
        // reads as a phone, not a card. Aspect close to a real
        // iPhone (19.5:9 ≈ 0.46) so the proportions feel right.
        width: 282,
        height: 600,
      }}
    >
      {/* Outer hardware ring */}
      <div
        className="absolute inset-0 rounded-[42px]"
        style={{
          background: "#0F1B33",
          boxShadow:
            "0 30px 60px -25px rgba(15,27,51,0.55), 0 8px 20px -10px rgba(15,27,51,0.3), inset 0 0 0 1px rgba(247,232,164,0.08)",
        }}
      />
      {/* Inner bezel + screen */}
      <div
        className="absolute overflow-hidden rounded-[34px]"
        style={{
          top: 8,
          right: 8,
          bottom: 8,
          left: 8,
          background: "#FAF6F0",
        }}
      >
        {/* Status bar — iOS-style chrome with notch pill in the
            middle, time on the left, signal/wifi/battery on the
            right. Sits over the screen at z-10 so notification
            stacks below render correctly. */}
        <StatusBar variant={variant} />
        {/* Screen content area below the status bar */}
        <div className="relative h-full pt-[34px]">{children}</div>
      </div>
    </div>
  );
}

function StatusBar({ variant }: { variant: "dark" | "light" }) {
  const isLight = variant === "light";
  // MS370: text + icon colour swaps for legibility over photo
  // wallpapers (Deliver). White-with-shadow matches real iOS lock-
  // screen status-bar rendering.
  const textColour = isLight ? "text-white" : "text-navy";
  const textShadow = isLight ? "0 1px 3px rgba(15,27,51,0.45)" : undefined;
  return (
    <div
      className="absolute left-0 right-0 top-0 z-10 flex items-center justify-between px-5"
      style={{
        height: 34,
        background: "transparent",
      }}
    >
      <span
        className={`text-[12px] font-semibold ${textColour}`}
        style={{ fontFamily: sansFamily, textShadow }}
      >
        9:41
      </span>
      {/* Notch pill */}
      <div
        aria-hidden="true"
        className="absolute left-1/2 -translate-x-1/2 rounded-full"
        style={{
          top: 6,
          width: 88,
          height: 22,
          background: "#0F1B33",
        }}
      />
      <div
        className={`flex items-center gap-1 ${textColour}`}
        style={{ filter: isLight ? "drop-shadow(0 1px 2px rgba(15,27,51,0.4))" : undefined }}
      >
        <Signal size={11} strokeWidth={2.4} />
        <Wifi size={12} strokeWidth={2.4} />
        <BatteryFull size={14} strokeWidth={2.2} />
      </div>
    </div>
  );
}

// ─── APP HEADER (re-used inside chat-style screens) ─────────────
// MS370: mirrors the landing-page nav at phone scale — real Lembra
// navy wordmark from /logo-navy.svg.
// MS371: dropped the coral "Send" pill per Lucky's call. Send is
// already the primary CTA at the bottom of Approve + Curate (chat-
// thread "Approve" beat), so a redundant Send pill in the nav added
// noise. Replaced with a subtle "Log in" link — same lg+ visibility
// pattern the live landing nav uses for its Login link, here always
// visible inside the mockup so visitors realise Lembra is a
// registered platform with an actual portal behind it.

function AppHeader() {
  return (
    <div
      className="flex items-center justify-between border-b px-3.5 py-2.5"
      style={{
        borderColor: "rgba(29,52,97,0.08)",
        background: "#FFF8E7",
      }}
    >
      {/* Wordmark — same /logo-navy.svg the landing nav uses, scaled
          to fit the phone-frame nav band. No <Link> wrapper since
          we're inside a mockup. */}
      <img
        src="/logo-navy.svg"
        alt="Lembra"
        height={18}
        style={{ height: 18, width: "auto", display: "block" }}
      />
      {/* MS371: "Log in" — subtle text link mirroring the landing
          nav's Login affordance. Surfaces that there's a registered
          platform behind the chat surface without competing with the
          in-screen Send/Approve CTAs. */}
      <span
        className="text-[11.5px] font-medium text-navy/70"
        style={{ fontFamily: sansFamily }}
      >
        Log in
      </span>
    </div>
  );
}

// ─── SCREEN 1 — BRIEF ────────────────────────────────────────────
// Chat with user bubble + AI preloader (real glyph morph + verbs)

// MS370: Brief now animates as a 3-beat sequence — (1) user types
// + sends, (2) AI thinking glyph + verb cycle appears, (3) AI reply
// resolves into a final message. Auto-loops via an `iter` counter
// that increments whenever Brief becomes active (parent passes
// isActive). Each animation cycle keys off `iter` so the bubbles
// re-mount and CSS animations restart. Previously the message was
// static — now the user sees the actual "type → think → respond"
// flow Lucky asked for.

// MS372: Brief animation restructured to fix the "user message bumps"
// glitch Lucky reported. Pre-MS372 had THREE separate bubbles
// (user, thinking, resolved) stacking in a justify-end column —
// each beat pushed the user bubble upward as new bubbles appeared
// below, causing 2-3 visible position shifts. Now the AI bubble
// is ONE container with grid-stacked thinking + resolved content;
// the content swaps via opacity crossfade inside the bubble at
// t=2400ms, so the user message only bumps ONCE (at t=900ms when
// the AI bubble first appears) and stays put for the rest of the
// cycle. The AI bubble's grid cell sizes to the taller (resolved)
// content so the bubble height never changes.
//
// Beats:
//   t=0       empty chat
//   t=200ms   user bubble slides in (anchored bottom)
//   t=900ms   AI bubble slides in with thinking content visible
//   t=2400ms  inside AI bubble: thinking content fades OUT, resolved
//             content fades IN simultaneously — bubble height stays
//             constant via the grid-stack so user doesn't shift
//   t=4200ms  parent crossfades to Curate
function BriefScreen({ isActive }: { isActive: boolean }) {
  const [verbIdx, setVerbIdx] = useState(0);
  const verbs = LEMBRA_VERB_VOCAB.conciergeRecommend;
  const reducedMotion = useReducedMotion();

  // `iter` increments each time Brief becomes active. Used as a key
  // on the chat thread so CSS animations re-run cleanly.
  const [iter, setIter] = useState(0);
  useEffect(() => {
    if (isActive) {
      setIter((n) => n + 1);
      setVerbIdx(0);
    }
  }, [isActive]);

  // Cycle verbs while thinking. Caller pauses this implicitly via
  // the iter-based remount when the screen transitions.
  useEffect(() => {
    if (reducedMotion || !isActive) return;
    const t = setInterval(() => {
      setVerbIdx((i) => (i + 1) % verbs.length);
    }, 700);
    return () => clearInterval(t);
  }, [reducedMotion, verbs.length, isActive, iter]);

  return (
    <div className="flex h-full flex-col">
      <AppHeader />
      {/* Messages anchor to the BOTTOM of the chat thread (iMessage
          convention). User bumps UP once at t=900ms when the AI
          bubble appears; after that everything stays static while
          the AI bubble's content morphs from thinking to resolved. */}
      <div
        key={iter}
        className="flex flex-1 flex-col justify-end space-y-2 overflow-hidden px-3.5 pb-3 pt-3"
      >
        {/* Beat 1 (t=200ms): User bubble slides up from the composer */}
        <div
          className="flex justify-end"
          style={{
            animation: "lembraBriefUserIn 520ms cubic-bezier(0.16, 1, 0.3, 1) both",
            animationDelay: "200ms",
          }}
        >
          <div
            className="max-w-[80%] rounded-2xl rounded-br-md px-3 py-2 text-[13px] leading-snug text-white"
            style={{
              background: "#C4564A",
              fontFamily: sansFamily,
            }}
          >
            Sarah&apos;s birthday next Friday. She&apos;s vegan and loves coffee.
          </div>
        </div>
        {/* Beats 2-3 (t=900ms slide-in, t=2400ms content swap):
            single AI bubble with grid-stacked thinking + resolved
            content. The grid cell sizes to the taller (resolved)
            content so the bubble height never changes — meaning
            after the bubble appears at t=900ms, the user bubble
            above doesn't move again. */}
        <div
          className="flex justify-start"
          style={{
            animation: "lembraBriefAiIn 520ms cubic-bezier(0.16, 1, 0.3, 1) both",
            animationDelay: "900ms",
          }}
        >
          <div
            className="inline-flex max-w-[82%] rounded-2xl rounded-bl-md px-3 py-2"
            style={{
              background: "rgba(29,52,97,0.06)",
              fontFamily: sansFamily,
            }}
          >
            <div className="grid">
              {/* Thinking content — visible 0-2400ms, fades out at swap */}
              <div
                className="flex items-center gap-2"
                style={{
                  gridArea: "1 / 1",
                  animation:
                    "lembraBriefAiThinkingOut 380ms cubic-bezier(0.16, 1, 0.3, 1) 2400ms forwards",
                }}
              >
                <CelebrationGlyphMorph size={16} colorClass="text-navy/75" />
                <span
                  key={verbIdx}
                  className="text-[12px] text-navy/75 whitespace-nowrap"
                  style={{ animation: "lembraVerbFade 360ms ease-out" }}
                >
                  {verbs[verbIdx]}
                </span>
              </div>
              {/* Resolved content — fades in at 2400ms in the same
                  grid cell, so the bubble doesn't grow or shrink. */}
              <div
                className="flex items-start gap-1.5 text-[13px] leading-snug text-navy"
                style={{
                  gridArea: "1 / 1",
                  opacity: 0,
                  animation:
                    "lembraBriefResolvedIn 380ms cubic-bezier(0.16, 1, 0.3, 1) 2400ms forwards",
                }}
              >
                <Sparkles
                  size={12}
                  strokeWidth={2.2}
                  className="text-coral shrink-0 mt-0.5"
                />
                <span>Got it. Three picks coming up for Sarah.</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Composer pinned to bottom — MS371: matches the actual live
          ComposerInput. rounded-2xl outer container with thin
          charcoal border (focus-within state in real composer);
          rounded-xl coral send button with the upright ArrowUp icon
          (not the angled Send/paper-plane). Mic-button-equivalent
          space stays empty in the mockup. */}
      <div
        className="flex items-end gap-2 border-t bg-white px-3 py-2.5"
        style={{ borderColor: "rgba(29,52,97,0.08)" }}
      >
        <div
          className="flex flex-1 items-center rounded-2xl border-2 px-3 py-2 text-[11.5px] text-navy/40"
          style={{
            borderColor: "rgba(45,41,38,0.15)",
            fontFamily: sansFamily,
          }}
        >
          Tell us about them…
        </div>
        <button
          type="button"
          aria-label="Send"
          className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl"
          style={{ background: "#C4564A" }}
        >
          <ArrowUp size={15} strokeWidth={2.4} className="text-white" />
        </button>
      </div>
    </div>
  );
}

// ─── SCREEN 2 — CURATE ───────────────────────────────────────────
// Chat with AI message above 3 stacked SKU cards using real photos

interface SkuCardData {
  vendor: string;
  product: string;
  price: string;
  meta: string;
  image: string;
  imageAlt: string;
  reason: string;
}

const CURATE_CARDS: SkuCardData[] = [
  {
    vendor: "MAKER & MONGER",
    product: "Cheese hamper",
    price: "$82",
    meta: "Cheese + beans + biscotti",
    image: "/vendor-photos/maker-and-monger/hampers1.webp",
    imageAlt: "Cheese and charcuterie hamper from Maker & Monger",
    reason: "Coffee fit",
  },
  {
    vendor: "PATRICIA COFFEE",
    product: "$80 voucher",
    price: "$80",
    meta: "Single origin · 1 year",
    image: "/vendor-photos/patricia-coffee/picnic-trio.jpg",
    imageAlt: "Patricia Coffee Brewers voucher",
    reason: "Her local",
  },
  {
    vendor: "LARKSPUR FLOWERS",
    product: "Native bouquet",
    price: "$68",
    meta: "Locally grown · vegan",
    image: "/vendor-photos/larkspur/flowers1.jpg",
    imageAlt: "Native flower bouquet from Larkspur",
    reason: "Vegan-safe",
  },
];

// MS371: Curate is now the explicit continuation of Brief's chat
// thread per Lucky's call. The same user message + AI resolved
// response from Brief appear at the top (carry-over context) and
// the 3 cards render as the AI's "here's what fits" attachment.
// A chat-style "Approve →" CTA sits at the bottom inside the chat
// thread — moves the approve action into the conversation flow,
// rather than nav-level, so the buyer feels like they're inside
// the chat rather than jumping to a separate confirm screen.

function CurateScreen() {
  return (
    <div className="flex h-full flex-col">
      <AppHeader />
      <div className="flex flex-1 flex-col space-y-2 overflow-hidden px-3.5 pb-3 pt-3">
        {/* MS372: carry-over copy now matches Brief's bubbles
            verbatim per Lucky's "make sure the message is the same"
            call. The two-phase chat thread reads as one continuous
            conversation — same user brief, same AI reply — with the
            3 cards below as the AI's follow-on attachment. */}
        <div className="flex justify-end">
          <div
            className="max-w-[80%] rounded-2xl rounded-br-md px-3 py-2 text-[13px] leading-snug text-white"
            style={{
              background: "#C4564A",
              fontFamily: sansFamily,
            }}
          >
            Sarah&apos;s birthday next Friday. She&apos;s vegan and loves coffee.
          </div>
        </div>
        <div className="flex justify-start">
          <div
            className="inline-flex max-w-[82%] items-start gap-1.5 rounded-2xl rounded-bl-md px-3 py-2 text-[13px] leading-snug text-navy"
            style={{
              background: "rgba(29,52,97,0.06)",
              fontFamily: sansFamily,
            }}
          >
            <Sparkles
              size={12}
              strokeWidth={2.2}
              className="text-coral shrink-0 mt-0.5"
            />
            <span>Got it. Three picks coming up for Sarah.</span>
          </div>
        </div>
        {/* The cards — rendered as the AI's "attachment" response,
            indented slightly to read as connected to the AI message
            above. */}
        <div className="flex flex-col space-y-1.5">
          {CURATE_CARDS.map((card) => (
            <CurateCard key={card.vendor} card={card} />
          ))}
        </div>
        {/* Chat-style "Approve" CTA pinned to the bottom of the
            thread per Lucky's call — Send/Approve actions live in
            the conversation flow, not in the nav. Reads as the
            buyer's reply to the AI's three-pick offering. */}
        <div className="mt-auto flex justify-end pt-1.5">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[12px] font-semibold text-white"
            style={{
              background: "#C4564A",
              fontFamily: sansFamily,
              boxShadow: "0 6px 16px -6px rgba(196,86,74,0.5)",
            }}
          >
            Approve
            <Check size={11} strokeWidth={2.6} />
          </button>
        </div>
      </div>
    </div>
  );
}

function CurateCard({ card }: { card: SkuCardData }) {
  return (
    <div
      className="flex items-stretch gap-2.5 overflow-hidden rounded-xl bg-white"
      style={{
        border: "1px solid rgba(29,52,97,0.08)",
        boxShadow: "0 1px 2px rgba(29,52,97,0.04)",
      }}
    >
      <div
        className="shrink-0 overflow-hidden"
        style={{
          width: 64,
          height: 64,
          background: "rgba(45,41,38,0.04)",
        }}
      >
        <img
          src={card.image}
          alt={card.imageAlt}
          loading="lazy"
          decoding="async"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = "none";
          }}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </div>
      <div className="flex flex-1 min-w-0 flex-col justify-center py-1.5">
        <p
          className="truncate text-[13.5px] font-semibold leading-tight text-navy"
          style={{ fontFamily: sansFamily }}
        >
          {card.product}
        </p>
        <p
          className="truncate text-[11px] text-navy/55"
          style={{ fontFamily: sansFamily, marginTop: 1 }}
        >
          {card.vendor.charAt(0) + card.vendor.slice(1).toLowerCase()} · {card.price}
        </p>
        <span
          className="mt-1 inline-flex w-fit items-center rounded-full px-1.5 py-0.5 text-[9px] uppercase tracking-wider"
          style={{
            background: "rgba(196,86,74,0.10)",
            color: "#C4564A",
            fontFamily: monoFamily,
          }}
        >
          {card.reason}
        </span>
      </div>
    </div>
  );
}

// ─── SCREEN 3 — APPROVE ──────────────────────────────────────────
// One chosen card with a coral "Send →" pulsing CTA below

function ApproveScreen() {
  const chosen = CURATE_CARDS[0]; // Maker & Monger hamper
  const ready = [
    "Card signed in your name",
    "Vendor briefed on dietary",
    "Courier booked for Friday",
  ];
  return (
    <div className="flex h-full flex-col">
      <AppHeader />
      <div className="flex flex-1 flex-col px-3.5 pt-3.5">
        {/* Hero card */}
        <div
          className="overflow-hidden rounded-2xl bg-white"
          style={{
            border: "2px solid #C4564A",
            boxShadow: "0 8px 24px -8px rgba(196,86,74,0.3)",
          }}
        >
          <div
            className="w-full overflow-hidden"
            style={{ aspectRatio: "16 / 9", background: "rgba(45,41,38,0.04)" }}
          >
            <img
              src={chosen.image}
              alt={chosen.imageAlt}
              loading="lazy"
              decoding="async"
              onError={(e) => {
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                display: "block",
              }}
            />
          </div>
          <div className="px-3 py-2.5">
            <p
              className="text-[9px] uppercase tracking-[0.14em] text-navy/55"
              style={{ fontFamily: monoFamily }}
            >
              {chosen.vendor}
            </p>
            <p
              className="mt-0.5 text-[14px] font-semibold leading-tight text-navy"
              style={{ fontFamily: sansFamily }}
            >
              {chosen.product}
            </p>
            <p
              className="mt-0.5 text-[11px] text-navy/60"
              style={{ fontFamily: sansFamily }}
            >
              {chosen.meta} · {chosen.price}
            </p>
          </div>
        </div>
        {/* MS369: replaced the single 3-clause status pill (which
            wrapped onto two lines at 9px) with a clean 3-row check
            list. Each row reads at full size + the green check tile
            on the left scans as "done". Sits in white card for clear
            grouping with the chosen hero above. */}
        <div
          className="mt-3 overflow-hidden rounded-xl bg-white"
          style={{ border: "1px solid rgba(29,52,97,0.08)" }}
        >
          {ready.map((line) => (
            <div
              key={line}
              className="flex items-center gap-2.5 border-b px-3 py-2 last:border-b-0"
              style={{ borderColor: "rgba(29,52,97,0.06)" }}
            >
              <span
                className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                style={{ background: "rgba(45,105,90,0.12)" }}
              >
                <Check size={11} strokeWidth={2.6} className="text-[#2D695A]" />
              </span>
              <p
                className="text-[12px] text-navy/80"
                style={{ fontFamily: sansFamily }}
              >
                {line}
              </p>
            </div>
          ))}
        </div>
        {/* MS371: dual CTA per Lucky's call — primary "Send to Sarah"
            paired with a secondary "Tweak" so the buyer can adjust
            even at the approval stage rather than feeling locked in.
            "Tweak" outlined navy on cream reads as a deliberate
            secondary action, not a cancel. Sits horizontally to the
            primary so the pair scans as "ship it OR refine it" —
            two equal-weight options the buyer chooses between. */}
        <div className="mt-auto flex items-center justify-center gap-2 pb-4 pt-4">
          <button
            type="button"
            className="inline-flex items-center gap-1 rounded-full border px-3.5 py-2 text-[12.5px] font-semibold text-navy"
            style={{
              borderColor: "rgba(29,52,97,0.18)",
              background: "white",
              fontFamily: sansFamily,
            }}
          >
            Edit
          </button>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white"
            style={{
              background: "#C4564A",
              fontFamily: sansFamily,
              animation: "lembraSendPulse 1800ms ease-in-out infinite",
              boxShadow: "0 8px 22px -6px rgba(196,86,74,0.45)",
            }}
          >
            Send to Sarah
            <Send size={13} strokeWidth={2.4} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── SCREEN 4 — DELIVER ──────────────────────────────────────────
// iOS lock-screen feel with stacked Lembra push notifications

interface PushNotif {
  time: string;
  title: string;
  body: string;
  highlight: boolean;
}

const DELIVER_NOTIFS: PushNotif[] = [
  {
    time: "2h ago",
    title: "Vendor confirmed",
    body: "Maker & Monger has Sarah’s hamper and the card.",
    highlight: false,
  },
  {
    time: "47m ago",
    title: "Out for delivery",
    body: "Courier picked up. ETA to Sarah’s desk 2:30 PM.",
    highlight: false,
  },
  {
    time: "now",
    title: "Delivered to Sarah’s desk",
    body: "2 minutes early. Recipient signed for it.",
    highlight: true,
  },
];

function DeliverScreen() {
  return (
    <div
      className="relative h-full overflow-hidden"
      style={{
        // MS370: replaced the warm-sunset CSS gradient with a real
        // mountain landscape photo (Unsplash, free hot-link). Phone
        // wallpapers in the wild are photos — a gradient read as a
        // brand chip even when warm-toned. Mountains-at-sunset is
        // the most iPhone-default-y aesthetic and supplies enough
        // light area at the bottom for the frosted-glass notification
        // chrome to sit cleanly.
        backgroundImage:
          "url(https://images.unsplash.com/photo-1469474968028-56623f02e42e?w=320&h=600&fit=crop&q=80)",
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Subtle dark scrim at the top so the white time + date stays
          readable across any wallpaper tint. iOS does the same. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0"
        style={{
          height: 140,
          background:
            "linear-gradient(180deg, rgba(15,27,51,0.32) 0%, rgba(15,27,51,0) 100%)",
        }}
      />
      {/* Lock-screen date + time */}
      <div className="relative flex flex-col items-center pt-5">
        <p
          className="text-[11px] font-medium text-white/90"
          style={{
            fontFamily: sansFamily,
            letterSpacing: "0.04em",
            textShadow: "0 1px 4px rgba(15,27,51,0.45)",
          }}
        >
          Monday, 18 August
        </p>
        <p
          className="mt-1 text-[54px] font-extralight leading-none text-white"
          style={{
            fontFamily: sansFamily,
            letterSpacing: "-0.04em",
            textShadow: "0 2px 8px rgba(15,27,51,0.4)",
          }}
        >
          2:30
        </p>
      </div>
      {/* Notification stack */}
      <div className="absolute inset-x-3 bottom-4 flex flex-col gap-1.5">
        {DELIVER_NOTIFS.map((n, i) => (
          <div
            key={n.title}
            className="rounded-2xl px-3 py-2 backdrop-blur-md"
            style={{
              background: "rgba(255,255,255,0.82)",
              border: "1px solid rgba(255,255,255,0.55)",
              boxShadow: "0 8px 18px -6px rgba(15,27,51,0.28)",
              animation: `lembraNotifSlideIn 480ms ease-out ${i * 140}ms both`,
            }}
          >
            <div className="flex items-start gap-2.5">
              {/* Lembra favicon — the actual apple-touch-icon asset
                  the live site serves, so the notif renders exactly
                  like an iOS push from the real app. */}
              <span
                className="inline-flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md"
                style={{
                  boxShadow: "0 1px 2px rgba(15,27,51,0.18)",
                }}
              >
                <img
                  src="/apple-touch-icon.png"
                  alt=""
                  width={28}
                  height={28}
                  loading="lazy"
                  decoding="async"
                  style={{ width: "100%", height: "100%", display: "block" }}
                />
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p
                    className="text-[10.5px] font-semibold text-navy"
                    style={{ fontFamily: sansFamily, letterSpacing: "0.02em" }}
                  >
                    LEMBRA
                  </p>
                  <p
                    className="text-[10px] text-navy/55"
                    style={{ fontFamily: sansFamily }}
                  >
                    {n.time}
                  </p>
                </div>
                {/* MS370: terminal "Delivered" notif gets the coral
                    check INLINE with the title (was an overlapping
                    badge in the top-right corner that collided with
                    the "now" timestamp). Reads as "Delivered ✓ to
                    Sarah's desk" — a confident tick next to the
                    sentence — rather than a stamp dropped on top. */}
                <p
                  className="mt-0.5 flex items-center gap-1.5 text-[12.5px] font-semibold leading-tight text-navy"
                  style={{ fontFamily: sansFamily }}
                >
                  {n.title}
                  {n.highlight && (
                    <span
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full"
                      style={{ background: "#C4564A" }}
                      aria-label="Delivered"
                    >
                      <Check size={9} strokeWidth={3} className="text-white" />
                    </span>
                  )}
                </p>
                <p
                  className="mt-0.5 text-[11px] leading-snug text-navy/70"
                  style={{ fontFamily: sansFamily }}
                >
                  {n.body}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── SCREEN ROUTER ───────────────────────────────────────────────

function PhaseScreen({ id, isActive }: { id: PhaseId; isActive: boolean }) {
  // MS370: Brief is the only screen with an entrance animation that
  // benefits from re-running when the phase becomes active. Other
  // screens render statically (Curate is a chat thread, Approve is a
  // confirm card, Deliver runs its own notif slide-in on initial mount).
  if (id === "brief") return <BriefScreen isActive={isActive} />;
  if (id === "curate") return <CurateScreen />;
  if (id === "approve") return <ApproveScreen />;
  return <DeliverScreen />;
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────

interface HowLembraWorksMobileProps {
  id?: string;
}

export function HowLembraWorksMobile({ id }: HowLembraWorksMobileProps) {
  const [activeIdx, setActiveIdx] = useState(0);
  const reducedMotion = useReducedMotion();
  // Auto-advance every PHASE_INTERVAL_MS, honour reduced-motion.
  useEffect(() => {
    if (reducedMotion) return;
    const t = setInterval(() => {
      setActiveIdx((i) => (i + 1) % PHASES.length);
    }, PHASE_INTERVAL_MS);
    return () => clearInterval(t);
  }, [reducedMotion]);

  return (
    <section
      id={id}
      className="scroll-mt-[96px] relative overflow-hidden"
      style={{
        background: "#1D3461",
        color: "#FAF6F0",
      }}
    >
      {/* Decorative butter glow top-right */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute"
        style={{
          top: -80,
          right: -80,
          width: 280,
          height: 280,
          background:
            "radial-gradient(circle, rgba(247,232,164,0.16) 0%, transparent 70%)",
        }}
      />

      {/* MS369: top padding 56 → 76 so the eyebrow + H2 don't sit
          flush against the section's upper edge. Bottom padding 64
          → 72 so the CTA has room to breathe before the next
          section's seam. */}
      <div className="relative" style={{ padding: "76px 24px 72px" }}>
        {/* Header */}
        <header className="mx-auto mb-7 text-center" style={{ maxWidth: 360 }}>
          <span
            className="inline-flex items-center gap-2.5"
            aria-hidden="true"
          >
            <span
              style={{
                display: "inline-block",
                width: 22,
                height: 1,
                background: "#F7E8A4",
              }}
            />
            <span
              style={{
                fontFamily: monoFamily,
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "#F7E8A4",
              }}
            >
              How Lembra works
            </span>
            <span
              style={{
                display: "inline-block",
                width: 22,
                height: 1,
                background: "#F7E8A4",
              }}
            />
          </span>
          <h2
            style={{
              fontFamily: displayFamily,
              fontStyle: "italic",
              fontWeight: 500,
              fontSize: "clamp(26px, 7.5vw, 32px)",
              lineHeight: 1.1,
              letterSpacing: "-0.022em",
              color: "#FAF6F0",
              margin: "10px 0 0",
            }}
          >
            Tell us about them.{" "}
            <span style={{ color: "#F7E8A4" }}>We do the rest.</span>
          </h2>
        </header>

        {/* Phone-frame mockup with crossfade between screens */}
        <div
          aria-live="polite"
          aria-atomic="false"
          className="relative mx-auto"
          style={{ width: 282, height: 600 }}
        >
          {PHASES.map((p, i) => {
            const isActive = i === activeIdx;
            return (
              <div
                key={p.id}
                aria-hidden={!isActive}
                className="absolute inset-0"
                style={{
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? "translateY(0)" : "translateY(8px)",
                  transition:
                    "opacity 520ms cubic-bezier(0.16, 1, 0.3, 1), transform 520ms cubic-bezier(0.16, 1, 0.3, 1)",
                  pointerEvents: isActive ? "auto" : "none",
                }}
              >
                <PhoneFrame variant={p.id === "deliver" ? "light" : "dark"}>
                  <PhaseScreen id={p.id} isActive={isActive} />
                </PhoneFrame>
              </div>
            );
          })}
        </div>

        {/* Pill row — all 4 modalities visible at once, with the
            active one highlighted (butter fill, navy text) and the
            other three in a subtle outline state. Tap any pill to
            jump to that phase; auto-cycle keeps running.
            MS369: replaced the single morphing pill per Lucky's
            "show all four" call. Sits with breathing room above
            the chrome (mt-8) so it doesn't crowd the phone frame. */}
        <div
          role="tablist"
          aria-label="How Lembra works phases"
          className="mt-8 flex items-center justify-center gap-1.5"
        >
          {PHASES.map((p, i) => {
            const isActive = i === activeIdx;
            return (
              <button
                key={p.id}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`mobile-phase-panel-${p.id}`}
                onClick={() => setActiveIdx(i)}
                className="inline-flex items-center rounded-full px-3 py-1.5 text-[11.5px] font-medium whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-butter"
                style={{
                  fontFamily: sansFamily,
                  background: isActive ? "#F7E8A4" : "transparent",
                  color: isActive ? "#1D3461" : "rgba(247,232,164,0.65)",
                  border: isActive
                    ? "1px solid #F7E8A4"
                    : "1px solid rgba(247,232,164,0.28)",
                  boxShadow: isActive
                    ? "0 4px 14px -6px rgba(247,232,164,0.5)"
                    : "none",
                  transition:
                    "background 320ms cubic-bezier(0.16, 1, 0.3, 1), color 320ms cubic-bezier(0.16, 1, 0.3, 1), border-color 320ms cubic-bezier(0.16, 1, 0.3, 1), box-shadow 320ms cubic-bezier(0.16, 1, 0.3, 1)",
                  fontWeight: isActive ? 600 : 500,
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>

        {/* Phase title (rotates with crossfade) */}
        <div className="mt-5 text-center" style={{ display: "grid" }}>
          {PHASES.map((p, i) => {
            const isActive = i === activeIdx;
            return (
              <h3
                key={p.id}
                aria-hidden={!isActive}
                style={{
                  gridRow: "1 / 2",
                  gridColumn: "1 / 2",
                  fontFamily: displayFamily,
                  fontStyle: "italic",
                  fontWeight: 500,
                  fontSize: "clamp(18px, 4.5vw, 21px)",
                  lineHeight: 1.2,
                  color: "#FAF6F0",
                  margin: "0 auto",
                  maxWidth: "26ch",
                  opacity: isActive ? 1 : 0,
                  transform: isActive ? "translateY(0)" : "translateY(4px)",
                  transition:
                    "opacity 420ms cubic-bezier(0.16, 1, 0.3, 1), transform 420ms cubic-bezier(0.16, 1, 0.3, 1)",
                  pointerEvents: "none",
                }}
              >
                {p.title}
              </h3>
            );
          })}
        </div>

        {/* CTA */}
        <div className="mt-8 flex justify-center">
          <Link
            to="/chat"
            className="inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3 text-sm font-medium text-white transition-all hover:bg-coral/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-coral focus-visible:ring-offset-2 focus-visible:ring-offset-navy"
            style={{ fontFamily: sansFamily }}
          >
            Send a gift →
          </Link>
        </div>
      </div>

      {/* Local keyframes — verb fade-in + send pulse + notif slide-in
          + Brief user/AI bubble entrances (MS370). */}
      <style>{`
        @keyframes lembraVerbFade {
          from { opacity: 0; transform: translateY(2px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes lembraSendPulse {
          0%, 100% { box-shadow: 0 8px 22px -6px rgba(196,86,74,0.45), 0 0 0 0 rgba(196,86,74,0.4); }
          50%      { box-shadow: 0 8px 22px -6px rgba(196,86,74,0.45), 0 0 0 8px rgba(196,86,74,0); }
        }
        @keyframes lembraNotifSlideIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes lembraBriefUserIn {
          from { opacity: 0; transform: translateY(10px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes lembraBriefAiIn {
          from { opacity: 0; transform: translateY(10px) scale(0.96); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes lembraBriefAiThinkingOut {
          from { opacity: 1; }
          to   { opacity: 0; }
        }
        @keyframes lembraBriefResolvedIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="lembraSendPulse"],
          [style*="lembraNotifSlideIn"],
          [style*="lembraVerbFade"],
          [style*="lembraBriefUserIn"],
          [style*="lembraBriefAiIn"],
          [style*="lembraBriefAiThinkingOut"],
          [style*="lembraBriefResolvedIn"] {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
}
